import React, { useEffect, useState } from 'react';
import { Gift, Share2 } from 'lucide-react';
import { api } from '../lib/api';
import ShareSheet from './ShareSheet';

/**
 * Reusable referral prompt card. Always opens the ShareSheet modal so the
 * share journey is consistent across desktop and mobile — no silent
 * clipboard-copy that leaves the user unsure what happened.
 *
 * Variants:
 *   `home`    — compact card for the Home tab top.
 *   `profile` — big button for Profile tab: "Invite a friend · Give 1, Get 2".
 */
export default function ReferralPromptCard({ variant = 'home', className = '', testid }) {
  const [refData, setRefData] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.myReferralCode()
      .then((d) => { if (alive) setRefData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!refData?.code) return null;

  const text = `Get a free month of FreeFitFuel — use my code ${refData.code} at signup. Adaptive AI training, meal plans and rehab that actually adapts to your body.`;
  const url = `${window.location.origin}/?ref=${refData.code}`;

  const openShare = () => setSheetOpen(true);

  if (variant === 'profile') {
    return (
      <>
        <button
          type="button"
          onClick={openShare}
          data-testid={testid || 'referral-profile-btn'}
          className={`w-full rounded-2xl border-2 border-[#BEFF38] bg-gradient-to-br from-[#BEFF38]/20 via-[#BEFF38]/10 to-transparent p-5 text-left hover:from-[#BEFF38]/25 transition-colors group ${className}`}
        >
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-[#BEFF38] text-black grid place-items-center shrink-0 shadow-lg group-hover:scale-105 transition-transform">
              <Gift className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-black uppercase tracking-tight text-lg leading-tight">
                Invite a friend · Give 1, Get 2
              </p>
              <p className="text-[13px] text-zinc-300 mt-1 leading-snug">
                They get {refData.friend_reward_days ?? 30} days free · You get {refData.referrer_reward_days ?? 60} days when they redeem.
              </p>
            </div>
            <Share2 className="w-6 h-6 text-[#BEFF38] shrink-0" />
          </div>
        </button>
        <ShareSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Give 1 month · Get 2 months"
          text={text}
          url={url}
        />
      </>
    );
  }

  // Home variant
  return (
    <>
      <div
        data-testid={testid || 'referral-home-card'}
        className={`rounded-2xl border border-[#BEFF38]/30 bg-gradient-to-r from-[#BEFF38]/10 to-transparent px-4 py-3 flex items-center gap-3 ${className}`}
      >
        <Gift className="w-5 h-5 text-[#BEFF38] shrink-0" strokeWidth={2.5} />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold leading-tight">
            <span aria-hidden="true">🎁 </span>Give 1 month free, get 2 months
          </p>
          <p className="text-[11px] text-zinc-400 mt-0.5 leading-tight">
            Share your code · {refData.redemption_count > 0 ? `${refData.redemption_count} redeemed` : 'Nobody yet — be first'}
          </p>
        </div>
        <button
          type="button"
          onClick={openShare}
          data-testid={testid ? `${testid}-share` : 'referral-home-share-btn'}
          className="h-9 px-3 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-[11px] inline-flex items-center gap-1.5 hover:bg-lime-300 shrink-0"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share
        </button>
      </div>
      <ShareSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Give 1 month · Get 2 months"
        text={text}
        url={url}
      />
    </>
  );
}
